
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const PRODUCTS_SCROLL_TRIGGER_START = "top top";
const PRODUCTS_SCROLL_TRIGGER_END = "bottom bottom";
const PRODUCTS_SCROLL_TO_DURATION = 0.2;
const PRODUCTS_SCROLL_TO_EASE = "power2.out";

/**
 * プロダクト紹介セクションのスクロール連動パネル切り替えを初期化する関数
 *
 * - スクロール量に応じてパネルとタブを自動で切り替える
 * - タブクリックでも手動で切り替え可能
 */
export function initScrollProducts() {
  "use strict";

  // --- 1. 必要な要素を取得 ---
  const outer = document.getElementById("products-scroll"); // パネル全体を囲む要素
  const panels = Array.from(document.querySelectorAll(".js-productsPanel")); // 各パネル
  const tabs = Array.from(
    document.querySelectorAll(".js-products__tab[data-product-idx]")
  ); // 各タブ

  // パネルがなければ何もしない
  if (!outer || panels.length === 0) {
    return;
  }

  let currentIndex = 0; // 現在表示中のパネル番号
  const panelCount = panels.length; // パネル枚数
  let trigger = null;

  // --- 2. パネル・タブの切り替え関数 ---
  /**
   * 指定したインデックスのパネル・タブをアクティブにする
   *
   * - すでに表示中なら何もしない
   * - パネルのis-activeクラスを付け替え
   * - タブのis-active/aria-selectedも切り替え
   */
  const switchPanel = (newIndex) => {
    if (newIndex === currentIndex) {
      return;
    }

    panels[currentIndex].classList.remove("is-active");
    panels[newIndex].classList.add("is-active");

    tabs.forEach((tab, i) => {
      tab.classList.toggle("is-active", i === newIndex);
      tab.setAttribute("aria-selected", i === newIndex ? "true" : "false");
    });

    currentIndex = newIndex;
  };

  // --- 3. ScrollTriggerでスクロール連動を初期化 ---
  /**
   * ScrollTriggerのprogress（0〜1）から現在のパネル番号を計算
   * progress * panelCount で「1画面分ごとにパネル切り替え」
   */
  const updateByProgress = (progress) => {
    const targetIndex = Math.max(
      0,
      Math.min(panelCount - 1, Math.floor(progress * panelCount))
    );
    switchPanel(targetIndex);
  };

  const createTrigger = () => {
    if (trigger) {
      trigger.kill();
      trigger = null;
    }

    // rangeが0以下の時は先頭パネルを維持
    const range = outer.offsetHeight - window.innerHeight;
    if (range <= 0) {
      switchPanel(0);
      return;
    }

    trigger = ScrollTrigger.create({
      trigger: outer,
      start: PRODUCTS_SCROLL_TRIGGER_START,
      end: PRODUCTS_SCROLL_TRIGGER_END,
      onUpdate: (self) => {
        updateByProgress(self.progress);
      },
    });
  };

  // --- 4. タブクリックで手動切り替え ---
  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      const idx = parseInt(tab.getAttribute("data-product-idx"), 10);
      if (!isNaN(idx)) {
        switchPanel(idx);

        // タブクリック時に、従来ロジックと同じ位置へスクロール
        const range = outer.offsetHeight - window.innerHeight;
        if (range > 0) {
          const targetScroll =
            outer.getBoundingClientRect().top +
            window.pageYOffset +
            (range * idx) / panelCount;

          // 監視も移動もGSAPに統一するため、ScrollToPluginで移動
          gsap.killTweensOf(window);
          gsap.to(window, {
            duration: PRODUCTS_SCROLL_TO_DURATION,
            ease: PRODUCTS_SCROLL_TO_EASE,
            scrollTo: {
              y: targetScroll,
              autoKill: true,
            },
          });
        }
      }
    });
  });

  // --- 5. 初期化・イベント登録 ---
  createTrigger();
  window.addEventListener("resize", createTrigger);
}
